import React from 'react';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { MessageTemplate } from '../services/api';
import { messageTemplateStatusStyle, templateStatusStyle } from '../utils/metaTemplateStatus';

/** Meta approval chip for a Campaigns message template (Official API only). */
export default function MessageTemplateMetaStatus({
  template,
  loading = false,
}: {
  template: MessageTemplate;
  /** Meta approval statuses are being refreshed from Meta. */
  loading?: boolean;
}) {
  if (loading) {
    return (
      <Chip
        label={<CircularProgress size={10} sx={{ color: '#ed8936', display: 'block' }} />}
        size="small"
        aria-label="Checking Meta status"
        sx={{ height: 18, minWidth: 48, bgcolor: 'var(--color-bg)', '& .MuiChip-label': { display: 'flex' } }}
      />
    );
  }
  const style = messageTemplateStatusStyle(template);
  return (
    <Tooltip title={style.hint} placement="top">
      <Chip
        label={`Meta: ${style.label}`}
        size="small"
        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: style.bg, color: style.color }}
      />
    </Tooltip>
  );
}

const SPEND_ESTIMATE_HINT = 'Estimated · S$0.10 per Official API marketing message';

function formatSgd(n: number): string {
  return `S$${Number(n || 0).toFixed(2)}`;
}

/** Spend so far for one message template. Same estimate as a campaign day: S$0.10 × Official API sends. */
export function MessageTemplateSpendChip({ cost }: { cost: number }) {
  return (
    <Tooltip title={SPEND_ESTIMATE_HINT} placement="top">
      <Chip
        label={`${formatSgd(cost)} spent`}
        size="small"
        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'var(--color-bg)', color: 'var(--color-text-faint)' }}
      />
    </Tooltip>
  );
}

const LEGEND: Array<{ style: ReturnType<typeof templateStatusStyle>; meaning: string }> = [
  {
    style: messageTemplateStatusStyle({ Status: null, MetaSynced: false }),
    meaning: 'not sent to Meta yet — edit and save it to submit',
  },
  { style: templateStatusStyle('PENDING'), meaning: 'Meta is reviewing it, usually within a day' },
  { style: templateStatusStyle('APPROVED'), meaning: 'ready to send to clients' },
  { style: templateStatusStyle('REJECTED'), meaning: 'Meta refused it — change the message and save again' },
];

/** Key for the Meta approval chips shown on message templates. */
export function MessageTemplateStatusLegend() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', fontWeight: 700 }}>
        Meta approval status — only Approved templates can be sent:
      </Typography>
      {LEGEND.map(({ style, meaning }) => (
        <Box key={style.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Meta: ${style.label}`}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: style.bg, color: style.color, flexShrink: 0 }}
          />
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>{meaning}</Typography>
        </Box>
      ))}
    </Box>
  );
}
